import { useRef, useState } from 'react';
import { useMessage } from '~/useMessage';

const baseUrl = `https://www.finn.no/distribution-front/external/carousel`;

type BlinkMessage = {
    match: boolean;
};

type LoadedMessage = {
    mainImageLoaded: boolean;
};

function isBlinkMessage(test?: object): test is BlinkMessage {
    return !!test && test.hasOwnProperty('match');
}

function isLoadingMessage(test?: object): test is LoadedMessage {
    return !!test && test.hasOwnProperty('mainImageLoaded');
}

function createUrl(retryCount: number, autoSwipe: boolean, adId?: string, channel?: string, position?: string) {
    if (adId) {
        return `${baseUrl}/preview?finnkode=${adId}&channel=${channel}&pos=${position}&retry=${retryCount}${
            autoSwipe ? '&autoSwipe=true' : ''
        }`;
    } else {
        return `${baseUrl}?channel=${channel}&pos=${position}&retry=${retryCount}${autoSwipe ? '&autoSwipe=true' : ''}`;
    }
}

type Props = {
    autoSwipe?: boolean;
    style?: React.CSSProperties;
    className?: string;
    channel?: string;
    position?: string;
    adId?: string;
    onLoad: () => void;
    onError: () => void;
};

export function BlinkFrame({
    autoSwipe = false,
    channel = 'finn-blink-landingpage',
    position = 'preview',
    adId,
    onLoad,
    onError,
    style,
    className,
}: Props) {
    const ref = useRef<HTMLIFrameElement>(null);
    const timer = useRef<ReturnType<typeof setTimeout>>();
    const loadedAndReady = useRef(false);
    const alive = useRef(false);

    const [retryCount, setRetryCount] = useState(1);

    const onPartialError = () => {
        if (!loadedAndReady.current) {
            if (retryCount > 2) {
                if (adId) {
                    const data = {
                        adId,
                        autoSwipe,
                        channel,
                        position,
                        type: 'PREVIEWLOADFAILURE',
                    };

                    fetch('/blink/report', {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify(data),
                    }).catch(() => undefined);
                }
                onError();
            } else {
                alive.current = false;
                setRetryCount((count) => count + 1);
            }
        }
    };

    useMessage(ref, (error: Error | null, data?: object | undefined) => {
        alive.current = true;

        if (timer.current) {
            // recieved message so iframe is alive clearing timer

            clearTimeout(timer.current);
            timer.current = undefined;
        }

        if (isBlinkMessage(data)) {
            if (data.match === false) {
                onPartialError();
            }
        } else if (isLoadingMessage(data)) {
            if (data.mainImageLoaded === true) {
                loadedAndReady.current = true;
                onLoad();
            }
        } else if (error) {
            onPartialError();
        }
    });

    const url = createUrl(retryCount, autoSwipe, adId, channel, position);

    return (
        <iframe
            style={style}
            ref={ref}
            title="Preview"
            className={`${className} z-50`}
            src={url}
            onLoad={() => {
                if (alive.current === false) {
                    timer.current = setTimeout(onPartialError, 1000 * retryCount);
                }
            }}
        />
    );
}
